import * as React from 'react';
import * as moment from 'moment';
import { AssignWorkNode, TownNode, AreaNode } from '../../../schema';
import './listPageStyles';

/**
 * One allocation, rendered as a card.
 *
 * Replaces the ten-column table the three work lists shared. A table gave
 * every field equal weight and buried the two things anyone actually looks
 * for -- how far along is this, and is it late -- in narrow columns. The card
 * leads with the caller, shows progress as a bar, and colours the deadline by
 * urgency.
 *
 * Used by Work List (a caller's own allocations, with a call button per
 * location), Assigned Work List (supervisor view, with reassignment) and
 * Finished Work List (read only).
 */

const initials = (name: string) => (name || '?')
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0].toUpperCase())
  .join('');

const statusKey = (status: string) => {
  switch (status) {
    case 'DOING': return 'doing';
    // The backend enum really is spelt FINNISHED.
    case 'FINNISHED': return 'finished';
    case 'ON_HOLD': return 'onhold';
    default: return 'pending';
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'DOING': return 'In progress';
    case 'FINNISHED': return 'Finished';
    case 'ON_HOLD': return 'On hold';
    default: return 'Not started';
  }
};

/** Total retailers across the areas if any were picked, else across the towns. */
export const totalRetailers = (work: AssignWorkNode) => {
  const source: Array<{ retailerCount?: number }> = (work.areas && work.areas.length)
    ? work.areas as any
    : (work.towns || []) as any;
  return source.reduce((sum, v) => sum + (Number(v.retailerCount) || 0), 0);
};

const dueDescriptor = (deadline: string, status: string) => {
  if (!deadline) {
    return { text: 'No deadline', modifier: '' };
  }
  const days = moment(deadline).startOf('day').diff(moment().startOf('day'), 'days');
  if (status === 'FINNISHED') {
    return { text: `Deadline ${moment(deadline).format('D MMM')}`, modifier: '' };
  }
  if (days < 0) {
    return { text: `Overdue by ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`, modifier: ' cs-alloc__due--overdue' };
  }
  if (days === 0) {
    return { text: 'Due today', modifier: ' cs-alloc__due--soon' };
  }
  if (days <= 2) {
    return { text: `Due in ${days} ${days === 1 ? 'day' : 'days'}`, modifier: ' cs-alloc__due--soon' };
  }
  return { text: `Due ${moment(deadline).format('D MMM')}`, modifier: '' };
};

export const AllocationCard: React.StatelessComponent<Props> = (props) => {
  const { work } = props;
  const status = work.workStatus as any as string;
  const key = statusKey(status);
  const total = totalRetailers(work);
  const called = Number(work.totalRetailersCalled) || 0;
  const pct = total ? Math.min(100, Math.round((called / total) * 100)) : 0;
  const due = dueDescriptor(work.deadline, status);
  const overdue = due.modifier.indexOf('overdue') !== -1;
  const callerName = (work.caller && work.caller.employee && work.caller.employee.name) || 'Unassigned';
  const locations: Array<TownNode | AreaNode> =
    (work.areas && work.areas.length ? work.areas : work.towns) as any || [];

  return (
    <div className={`cs-alloc cs-alloc--${overdue && status !== 'FINNISHED' ? 'overdue' : key}`}>
      <div className='cs-alloc__head'>
        <span className='cs-alloc__avatar' aria-hidden='true'>{initials(callerName)}</span>
        <span className='cs-alloc__ident'>
          <h4 className='cs-alloc__name'>{callerName}</h4>
          <p className='cs-alloc__where'>
            {work.state && work.state.name}
            {locations.length ? ' · ' : ''}
            {locations.map((v) => v.name).join(', ')}
          </p>
        </span>
        <span className={`cs-alloc__status cs-alloc__status--${key}`}>{statusLabel(status)}</span>
      </div>

      {/* Progress is the headline number a supervisor scans for; in the table
          it was two adjacent integers in separate columns. */}
      <div className='cs-alloc__progress'>
        <div className={`cs-alloc__bar${pct >= 100 ? ' cs-alloc__bar--done' : ''}`}>
          <span style={{ width: `${pct}%` }} />
        </div>
        <div className='cs-alloc__figures'>
          <span><b>{called}</b> of {total} retailers called</span>
          <span>{pct}%</span>
        </div>
      </div>

      {props.locationAction && locations.length ? (
        <div className='cs-alloc__locs'>
          {locations.map((loc) => (
            <div className='cs-alloc__loc' key={loc.id}>
              <span>
                <span className='cs-alloc__loc-name'>{loc.name}</span>
                <br />
                <span className='cs-alloc__loc-sub'>
                  {(loc as any).retailerCount} {(loc as any).retailerCount === 1 ? 'retailer' : 'retailers'}
                </span>
              </span>
              {props.locationAction(loc)}
            </div>
          ))}
        </div>
      ) : null}

      <div className='cs-alloc__foot'>
        <span className='cs-alloc__dates'>
          <span>Assigned {work.dateAssigned ? moment(work.dateAssigned).format('D MMM YYYY') : '--'}</span>
          <span className={`cs-alloc__due${due.modifier}`}>{due.text}</span>
        </span>
        {props.actions ? <span className='cs-alloc__actions'>{props.actions}</span> : null}
      </div>

      {props.reassign ? <div className='cs-alloc__reassign'>{props.reassign}</div> : null}
    </div>
  );
};

interface Props {
  work: AssignWorkNode;
  /** Rendered per town/area -- Work List uses this for its call buttons. */
  locationAction?: (location: TownNode | AreaNode) => React.ReactNode;
  /** Footer-right controls. */
  actions?: React.ReactNode;
  /** Supervisor-only reassignment strip. */
  reassign?: React.ReactNode;
}
